import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
/*
 * ⚠ THE PANEL'S STRUCTURE COMES FROM THE MANIFEST NOW, not from `adminPanelTools.js`.
 *
 * `panelToolsForTab` and `panelGroupsForTab` derived the tabs and their sub-headings from a
 * `tab` and a `group` field on each of twenty-five scattered tool objects. Membership and layout
 * are ONE decision — "what is on this tab, under what heading" — so they are made in one place,
 * and what is left here is genuinely per-tool: the label, the blurb, and whether the control
 * needs a clicked bar.
 */
import { TAB_CYCLES, TAB_GEOMETRY, TAB_SYSTEM, PANEL_TOOL_DETAIL }
  from '../config/adminPanelTools';
import { TOOL_REGISTRY } from '../config/toolRegistry';
import { useManifest } from '../context/ManifestContext';
import { SCAN_MARKETS } from '../utils/marketScanner';
import { ASTRO, INTEGRALDELTA, ADMIN, PRODUCT_TARGETS } from '../config/productTarget';
import { CTS_COOLDOWN_BARS } from '../utils/ctsDeduplication';

/**
 * What each target is CALLED on screen, which is not what it is called on the wire.
 *
 * <p>The stored values are lower-case single words (`integraldelta`) because they are a config
 * key; a reader needs the product's actual name. Keyed off the exported constants rather than
 * retyped strings, so a renamed target is a compile-time concern and not a label that silently
 * falls back to its own id.
 */
const PRODUCT_LABELS = {
  [ASTRO]: 'AstroStockCharts',
  [INTEGRALDELTA]: 'integralDelta',
  [ADMIN]: 'Internal (all)',
};

/**
 * THE ADMIN PANEL: the diagnostic tools that used to crowd the main toolbar.
 *
 * <h2>It invokes through the toolbar's own dispatcher</h2>
 *
 * Every relocated tool is a button carrying the same {@code action} string its toolbar icon
 * carried, and pressing it calls {@code onAction(action)} — which is TabPanel's
 * {@code handleIconClick}. So a relocated tool does literally what it did before. The alternative,
 * passing eleven callbacks in, would be a second copy of routing that already exists and would
 * drift the first time one handler was fixed.
 *
 * <h2>Admin-only, enforced by the caller AND by this component</h2>
 *
 * TabPanel does not render the opening icon for a non-admin, and this returns null for one too.
 * Two gates rather than one because they fail differently: the icon can be hidden while the dialog
 * is still reachable through a stale {@code open} prop, and a dialog that trusts its caller is one
 * refactor away from being opened by something that forgot.
 */
const AdminPanelDialog = ({
  open, onClose, isAdmin, onAction, anchorIndex, slots = {},
  onZoomFirstYear, tradeCardCount, openTradeCardCount, onSetAllTradeCards,
  yearCount, summariesShown, onSetYearlySummaries, onEnterPresentationMode,
  onOpenDeepPivot,
  onOpenCelestialAspects,
  onRunMidpointTrineScan,
  onRunEclipseStudy,
  onRunMarketScanner,
  onOpenPlanetaryAnalog,
  onOpenPivotCommonalities,
  onOpenChartEndDates,
  // ⚠ DEFAULT PARAMETERS, not `defaultProps`, for the three new ones. `defaultProps` warns on
  // every render of a function component in this React version; the existing block below is
  // pre-existing and left alone rather than migrated in a commit about a product switcher.
  product = ADMIN,
  buildProduct = ADMIN,
  onProductChange = undefined,
  ctsCooldownBars = CTS_COOLDOWN_BARS,
  onCtsCooldownBarsChange = undefined,
}) => {
  const [note, setNote] = useState('');
  // Defaults: the first market, and an EMPTY date meaning "that market's latest
  // session". Empty rather than today's date, because today resolved on a browser
  // clock is the wrong trading day for India for most of its session -- the backend
  // knows each market's own calendar and this does not.
  const [scannerMarket, setScannerMarket] = useState(SCAN_MARKETS[0].value);
  const [scannerDate, setScannerDate] = useState('');
  const [scannerBusy, setScannerBusy] = useState(false);
  /*
   * WHICH TAB IS OPEN, by tab ID rather than by index.
   *
   * An index would be a second declaration of the manifest tab order: reorder it and every
   * remembered tab silently becomes a different one. The id is the tab.
   *
   * <p>Held across OPEN AND CLOSE, because this dialog is mounted for the life of the page — a
   * reader who was working in Price & Geometry and reopens the panel is still working there.
   */
  const [tab, setTab] = useState(TAB_CYCLES);

  /**
   * THE PANEL THIS SESSION'S MANIFEST DECLARES.
   *
   * <h2>⚠ EMPTY FOR A CUSTOMER MANIFEST, AND THAT IS CORRECT</h2>
   *
   * `adminPanel` is `null` on astro, dhanabhyas and integralDelta, so `manifestTabs` is `[]` and
   * this dialog renders a tab strip with nothing in it. A customer can never reach that state —
   * the toolbar button is `admin_panel`, which only the Studio names, and the `isAdmin` guard
   * below is the second lock. Defaulting to the admin panel here would be a third answer to
   * "which product is this", and the one that could silently be wrong.
   */
  const manifest = useManifest();
  const manifestTabs = (manifest.adminPanel && manifest.adminPanel.tabs) || [];
  const manifestTab = (tabId) => manifestTabs.find((t) => t.id === tabId);

  if (!isAdmin) return null;

  const run = (action) => {
    onAction(action);
    // The panel STAYS OPEN. Several of these are toggles a reader flicks on and off while
    // watching the chart, and a panel that closed on every press would make comparing two of them
    // a chore. The dialog is deliberately not modal-blocking for the same reason.
    setNote('');
  };

  const allShown = tradeCardCount > 0 && openTradeCardCount >= tradeCardCount;

  /**
   * Whether a tool is refused for want of a clicked bar.
   *
   * <p>Read from the tool's own {@code needsAnchor} rather than from a list of action names here,
   * so the declaration and the guard cannot disagree — the same rule the relocation set follows.
   */
  const blockedForAnchor = (tool) => !!tool.needsAnchor && !Number.isInteger(anchorIndex);

  /**
   * The dispatched tools on one tab, under their own sub-headings.
   *
   * <p>ONE renderer called three times rather than three copies of the loop — which is what the
   * flat panel effectively had, and is how a change to a tool row comes to be made in two of the
   * three places. Both the groups and the tools are filtered by tab in `adminPanelTools.js`, so
   * this knows nothing about which tab holds what.
   *
   * <p>Returns nothing for a tab with no dispatched tools, so an empty section heading cannot
   * appear — `panelGroupsForTab` yields no groups and the map is empty.
   */
  /**
   * One manifest tool id, resolved to everything the row needs.
   *
   * <p>⚠ THE REGISTRY SUPPLIES THE ACTION, the detail map supplies the prose. A tool with no
   * detail entry still renders — from its registry label, with no blurb — which is the honest
   * degradation: a missing sentence is a thinner row, where a missing DESCRIPTOR used to mean a
   * tool that did not appear at all. That is not hypothetical; it is what hid seven admin tools
   * between Stage 1 and Stage 2.
   *
   * <p>Returns null only for an id the registry does not know, which a test makes impossible.
   */
  const resolveTool = (toolId) => {
    const entry = TOOL_REGISTRY[toolId];
    if (!entry) return null;
    const detail = PANEL_TOOL_DETAIL[entry.actionId] || {};
    return {
      action: entry.actionId,
      label: detail.label || entry.label,
      blurb: detail.blurb,
      needsAnchor: !!detail.needsAnchor,
    };
  };

  const toolGroups = (tabId) => (manifestTab(tabId)?.groups || []).map((group) => (
    <Box key={group.label} sx={{ mb: 1.5 }}>
      <Divider sx={{ mb: 1 }} />
      <Typography variant="overline" sx={{ color: 'text.secondary' }}>{group.label}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.5 }}>
        {group.tools.map(resolveTool).filter(Boolean).map((tool) => (
          <Box key={tool.action}>
            {/*
              * ⚠ THE TOOLTIP WRAPS A SPAN, not the Button.
              *
              * A disabled MUI Button sets `pointer-events: none`, so a Tooltip anchored directly
              * to it never fires — and the reader would be left with a greyed control and no
              * explanation, which is strictly worse than an enabled one that refuses. The span is
              * the documented way to keep the hover target alive.
              *
              * Always wrapped, so the enabled and disabled paths render the same tree and a
              * difference in behaviour cannot come from a difference in structure.
              */}
            {/*
              * ⚠ A SLOT WINS OVER THE BUTTON, for the tools that are not dispatched ACTIONS.
              *
              * Every ordinary entry here names a `case` in `actionDispatcher.js` and a plain
              * Button calling `onAction` is the whole of it. Run Global Scan and Auto-Analysis
              * are self-contained COMPONENTS that own their button, their dialog and their
              * progress state, so the panel renders the component rather than a button that
              * would have to reach inside it. Lifting their state out to make them dispatchable
              * would be a rewrite of two working controls for no gain.
              *
              * ⚠ A slotted control is mounted BY THIS DIALOG, so closing the panel unmounts its
              * progress view. Both scans run on the backend, so what is lost is the view and not
              * the scan — and the panel deliberately stays open on an action, which is the case
              * these need.
              */}
            {slots[tool.action] || (
            <Tooltip
              title={blockedForAnchor(tool) ? 'Click a candle bar first' : ''}
              disableHoverListener={!blockedForAnchor(tool)}>
              <span>
                <Button variant="text" size="small" data-testid={`admin-tool-${tool.action}`}
                  onClick={() => run(tool.action)}
                  disabled={blockedForAnchor(tool)}
                  sx={{ justifyContent: 'flex-start', textAlign: 'left', width: '100%', py: 0.25 }}>
                  {tool.label}
                </Button>
              </span>
            </Tooltip>
            )}
            <Typography variant="caption"
              sx={{ display: 'block', color: 'text.secondary', pl: 1, mt: -0.25 }}>
              {tool.blurb}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  ));

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth
      // Not modal: the chart underneath is the thing these tools act on, so it must stay visible
      // and the backdrop must not grey it out.
      hideBackdrop
      sx={{ '& .MuiDialog-paper': { position: 'fixed', right: 24, top: 96, m: 0, maxHeight: '80vh',
        boxShadow: 6, border: '1px solid', borderColor: 'divider' } }}
      data-testid="admin-panel-dialog"
    >
      <DialogTitle sx={{ pb: 1, fontSize: 16, fontWeight: 700 }}>
        Admin Panel
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.25 }}>
          Diagnostics and research tools. Not shown to customers.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 1.5 }}>
      {/* ── THE THREE TABS ─────────────────────────────────────────────────────────────────
          The panel was a flat scroll of eleven sections; a reader looking for one tool read past
          the other ten. Three tabs, with the assignment declared on each tool in
          `adminPanelTools.js` rather than implied by where its JSX sits. */}
      <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth"
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, fontSize: 12, py: 0.5 } }}
        data-testid="admin-panel-tabs">
        {manifestTabs.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} data-testid={`admin-tab-btn-${t.id}`} />
        ))}
      </Tabs>

      {/*
        ⚠ EVERY PANEL STAYS MOUNTED, hidden with `display: none` plus the `hidden` attribute.
        This is one form with three views. The CTS cooldown field, the scanner's market and date,
        and the product-target radio all hold state, and an unmounted panel would discard whatever
        a reader had typed the moment they looked at another tab -- which is the brief's own
        requirement that parameter fields survive a tab switch. The same arrangement the DynaMan
        setup dialog uses, and for the same reason.

        It also keeps every `data-testid` in the document at all times, so the fifteen existing
        tests that reach for these controls by id are unaffected by the tabs.
      */}
      <Box role="tabpanel" hidden={tab !== 'cycles'} data-testid="admin-tab-cycles"
        sx={{ display: tab === 'cycles' ? 'block' : 'none' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Confluence studies</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 1.5 }}>
          <Button variant="outlined" size="small" data-testid="admin-celestial-aspects"
            onClick={() => { onOpenCelestialAspects && onOpenCelestialAspects(); onClose(); }}>
            Deep Celestial Research — trine confluence
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Precise conjunctions, squares, trines and oppositions between twelve bodies, filed by
            day on an 18:00 ET cutoff. Marks the days where a trine meets a selected aspect; an
            aspect on a non-trading day is drawn higher, on the next bar.
          </Typography>

          <Button variant="outlined" size="small" data-testid="admin-midpoint-trine"
            onClick={() => {
              // The panel STAYS OPEN, unlike the two above it. Those hand over to a dialog of
              // their own; this one paints the chart underneath and leaves it, so closing would
              // hide the panel to reveal a result the reader then has to reopen it to re-run.
              const result = onRunMidpointTrineScan && onRunMidpointTrineScan();
              if (result && typeof result.then === 'function') {
                setNote('Scanning the loaded range...');
                result.then((msg) => setNote(msg || ''))
                  .catch((e) => setNote(`The scan failed: ${e && e.message ? e.message : e}`));
              } else {
                setNote(result || '');
              }
            }}>
            Midpoint Trine Scan
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Days on which three bodies stand mutually 120&deg; apart within 1&deg; — each one at
            the far midpoint of the other two. Sampled twice an hour, so the Moon, Mercury and
            Venus are not stepped over. Marks sit on the rail: the scan knows when, not which way.
          </Typography>

          <Button variant="outlined" size="small" data-testid="admin-eclipse-study"
            onClick={() => {
              // STAYS OPEN, as the midpoint scan does and for the same reason: this paints the
              // chart underneath rather than handing over to a dialog, so closing the panel would
              // hide it to reveal a result the reader has to reopen it to re-run.
              const result = onRunEclipseStudy && onRunEclipseStudy();
              if (result && typeof result.then === 'function') {
                setNote('Running the eclipse study over the loaded range...');
                result.then((msg) => setNote(msg || ''))
                  .catch((e) => setNote(`The study failed: ${e && e.message ? e.message : e}`));
              } else {
                setNote(result || '');
              }
            }}>
            Eclipse Study
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Each solar eclipse names an eclipse planet — the body aspecting the Sun to its left —
            and the nine tracking rules then follow it to the next eclipse. Every event is tagged
            <code> [Marker: Description]</code> in its tooltip. A circle marks an eclipse, a tick a
            bar carrying events; both sit on the rail, because every rule here answers when.
          </Typography>

          <Button variant="outlined" size="small" data-testid="admin-planetary-analog"
            onClick={() => { onOpenPlanetaryAnalog && onOpenPlanetaryAnalog(); onClose(); }}>
            Planetary Analog Scanner
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Historical days whose planetary signature resembles today&apos;s — geocentric
            aspects, heliocentric phase angles, sidereal Navamsha, declination parallels,
            distance extrema and station degree returns — ranked by how improbable the
            agreement is. <strong>Research only: the outcome is unproven</strong>, and the
            panel reports the forward edge against the instrument&apos;s own base rate so it
            can be judged rather than assumed.
          </Typography>

          <Button variant="outlined" size="small" data-testid="admin-pivot-commonalities"
            onClick={() => { onOpenPivotCommonalities && onOpenPivotCommonalities(); onClose(); }}>
            Pivot Planetary Commonalities Study
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            The loaded symbol&apos;s major swing highs and lows, and every planetary recurrence
            between them &mdash; same-planet longitude returns, cross-planet degree transfers and
            Navamsha parity &mdash; measured strictly pivot to pivot, with no intervening bar read
            by any rule. Selecting a match marks both pivot dates on the chart.
            <strong> Research only</strong>: a row&apos;s score measures how improbable the
            agreement is across the pairs compared, which is coincidence rather than a forward
            edge, and nothing here predicts anything.
          </Typography>
        </Box>

        {/* ── THE CTS COOLDOWN ───────────────────────────────────────────────────────────────
            Here rather than on the toolbar because it is an instrument, not a setting a reader
            should meet: it changes how much of the scan reaches the chart, and the honest default
            is the shipped one. */}
        <Divider sx={{ mb: 1 }} />
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>CTS cooldown</Typography>
        <Box sx={{ mt: 0.5, mb: 1.5 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75 }}>
            Bars a CTS mark silences after it. The shipped value is 4.
            <strong> Set 0 to draw every flagged bar</strong> — the scan unfolded.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small" type="number" label="Bars" value={ctsCooldownBars}
              onChange={(e) => {
                // ⚠ CLAMPED, and an empty field reads as 0 rather than NaN. `foldCtsHits` treats
                // any value <= 0 as "no fold", so NaN would silently behave like 0 and the reader
                // would have no way to tell a cleared box from a deliberate zero.
                const n = Number.parseInt(e.target.value, 10);
                const next = Number.isFinite(n) ? Math.max(0, Math.min(60, n)) : 0;
                if (onCtsCooldownBarsChange) onCtsCooldownBarsChange(next);
              }}
              inputProps={{ min: 0, max: 60, 'data-testid': 'admin-cts-cooldown' }}
              sx={{ width: 110 }}
            />
            <Button variant="outlined" size="small" data-testid="admin-cts-cooldown-off"
              disabled={ctsCooldownBars === 0 || !onCtsCooldownBarsChange}
              onClick={() => onCtsCooldownBarsChange(0)}>
              Show all
            </Button>
            <Button variant="text" size="small" data-testid="admin-cts-cooldown-reset"
              disabled={ctsCooldownBars === CTS_COOLDOWN_BARS || !onCtsCooldownBarsChange}
              onClick={() => onCtsCooldownBarsChange(CTS_COOLDOWN_BARS)}>
              {`Reset to ${CTS_COOLDOWN_BARS}`}
            </Button>
          </Box>
          {ctsCooldownBars !== CTS_COOLDOWN_BARS && (
            // ⚠ SAYS SO, because a chart carrying far more or far fewer marks than usual with
            // nothing on screen explaining it is the kind of thing that gets reported as a bug.
            <Alert severity="warning" sx={{ mt: 1, py: 0 }} data-testid="admin-cts-cooldown-note">
              {ctsCooldownBars === 0
                ? 'Cooldown OFF — every flagged bar is drawn. Re-run the scan to see it.'
                : `Cooldown is ${ctsCooldownBars} bars, not the shipped ${CTS_COOLDOWN_BARS}.`
                  + ' Re-run the scan to apply it.'}
            </Alert>
          )}
        </Box>

        {toolGroups(TAB_CYCLES)}
      </Box>
      <Box role="tabpanel" hidden={tab !== 'geometry'} data-testid="admin-tab-geometry"
        sx={{ display: tab === 'geometry' ? 'block' : 'none' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Price structure</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 1.5 }}>
          <Button variant="outlined" size="small" data-testid="admin-deep-pivot"
            onClick={() => { onOpenDeepPivot && onOpenDeepPivot(); onClose(); }}>
            Run Deep Pivot Celestial Scan
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Every 3-bar swing pivot, its leg geometry, and the aspects over it — with a CSV
            export for correlation work.
          </Typography>
        </Box>

        {toolGroups(TAB_GEOMETRY)}
      </Box>
      <Box role="tabpanel" hidden={tab !== 'system'} data-testid="admin-tab-system"
        sx={{ display: tab === 'system' ? 'block' : 'none' }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Chart</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 1.5 }}>
          <Button variant="outlined" size="small" data-testid="admin-zoom-first-year"
            onClick={() => {
              const result = onZoomFirstYear && onZoomFirstYear();
              // NAMED, not silent. "Nothing happened" and "there is no data loaded" look identical
              // on a chart, and only one of them is a problem.
              setNote(result
                ? `Zoomed to ${result.year} — ${result.from} to ${result.to} (${result.bars} bars).`
                : 'No series is loaded, so there is no first year to zoom to.');
            }}>
            Zoom to First Calendar Year
          </Button>

          <Button variant="outlined" size="small" data-testid="admin-toggle-all-cards"
            disabled={tradeCardCount === 0}
            onClick={() => {
              const shown = onSetAllTradeCards && onSetAllTradeCards(!allShown);
              setNote(shown
                ? `Showing ${shown} trade card${shown === 1 ? '' : 's'}.`
                : 'All trade cards hidden.');
            }}>
            {allShown ? 'Hide All Trade Cards' : 'Show All Trade Cards'}
            {tradeCardCount > 0 && ` (${tradeCardCount})`}
          </Button>
          {tradeCardCount === 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
              Run an engine first — there are no trades drawn to card.
            </Typography>
          )}

          <Button variant="outlined" size="small" data-testid="admin-yearly-summaries"
            disabled={yearCount === 0}
            onClick={() => {
              const shown = onSetYearlySummaries && onSetYearlySummaries(!summariesShown);
              setNote(shown
                ? `Showing ${shown} yearly summary plaque${shown === 1 ? '' : 's'}.`
                : 'Yearly summaries hidden.');
            }}>
            {summariesShown ? 'Hide Yearly Summaries' : 'Show Yearly Summaries'}
            {yearCount > 0 && ` (${yearCount})`}
          </Button>
          {yearCount === 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
              Run an engine first — there are no closed years to summarise.
            </Typography>
          )}

          <Button variant="outlined" size="small" data-testid="admin-presentation-mode"
            onClick={() => { onEnterPresentationMode && onEnterPresentationMode(); onClose(); }}>
            Presentation Mode
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Hides the toolbar so the canvas is the whole screen. Press <b>P</b> or <b>Esc</b> to
            come back — there is no button once it is hidden.
          </Typography>

          {/*
            SOFT-DELETED: the "Hide the price series" switch, removed from the panel on request.

            The MECHANIC is untouched. `withPriceHidden` and the view-mode cycle in
            utils/chartViewMode.js are unchanged, and the state the switch drove still exists in
            the caller -- so restoring this is re-adding a switch, not rebuilding a feature.

            Its two props were dropped from this component because an unused prop is an eslint
            error, not a comment. The caller still owns the state; see TabPanel.
          */}
        </Box>

        <Divider sx={{ mb: 1 }} />
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Scanner</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5, mb: 1.5 }}>
          {/* THE DAILY EOD SCANNER. Its own block, because unlike every other tool here it
              takes PARAMETERS — a market and a date — and a button that silently scanned
              whichever market was last used would make a reported candidate list ambiguous
              about what it was a list OF. */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              select size="small" label="Market" value={scannerMarket}
              onChange={(e) => setScannerMarket(e.target.value)}
              sx={{ width: 190, flex: '0 0 auto' }}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'scanner-market' }}
            >
              {SCAN_MARKETS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </TextField>
            <TextField
              size="small" type="date" label="Scan date" value={scannerDate}
              onChange={(e) => setScannerDate(e.target.value)}
              sx={{ width: 160, flex: '0 0 auto' }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ 'data-testid': 'scanner-date' }}
            />
            <Button variant="outlined" size="small" data-testid="admin-market-scanner"
              disabled={scannerBusy}
              onClick={() => {
                // STAYS OPEN, like the other in-place tools: this paints a result the reader
                // then acts on rather than handing over to a dialog that replaces the panel.
                setScannerBusy(true);
                setNote('Running the market scanner...');
                const done = (msg) => { setScannerBusy(false); setNote(msg || ''); };
                const result = onRunMarketScanner
                  && onRunMarketScanner(scannerMarket, scannerDate || undefined);
                if (result && typeof result.then === 'function') {
                  result.then(done).catch((e) =>
                    done(`The scan failed: ${e && e.message ? e.message : e}`));
                } else {
                  done(result);
                }
              }}>
              {scannerBusy ? 'Scanning…' : 'Run Market Scanner'}
            </Button>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Every symbol in the market, checked against the four top and four bottom lines of
            both PTA and PTB within 0.25%. Results are stored in Firestore under
            <code> market_scanner_results</code> and the candidates open for review. Leave the
            date empty for the market&apos;s own latest session &mdash; a non-trading day
            legitimately returns nothing, because a symbol is only judged on a bar it actually
            has.
          </Typography>

          <Button variant="outlined" size="small" data-testid="admin-chart-end-dates"
            onClick={() => { onOpenChartEndDates && onOpenChartEndDates(); onClose(); }}>
            Chart End Date by Audience
          </Button>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: -0.5 }}>
            Where each audience&apos;s charts stop, one date per role. A date in the past
            truncates history; one in the future extends the forward axis for study. Enforced on
            the server, so it applies to every study as well as the chart.
          </Typography>
        </Box>

        {/* ── THE PRODUCT TARGET ─────────────────────────────────────────────────────────────
            Placed directly after the chart actions and BEFORE the relocated tools, because it
            changes which of those tools are on the toolbar at all. A switcher below the list it
            filters would be read after the reader had already wondered where an icon went. */}
        <Divider sx={{ mb: 1 }} />
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>Product target</Typography>
        <Box sx={{ mt: 0.5, mb: 1.5 }}>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.75 }}>
            What the toolbar and the Copilot are gated by. Inspection only — every endpoint keeps
            its own authorisation, so switching reveals surfaces, never permissions.
          </Typography>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={product}
            onChange={(e, next) => {
              // ⚠ MUI SENDS `null` WHEN THE ACTIVE BUTTON IS CLICKED AGAIN. Without this guard an
              // exclusive group deselects, `writeProductOverride` is handed null, and the panel
              // silently resets to the baked target — a click on the CURRENT product undoing the
              // override is the opposite of a no-op.
              if (!next || next === product) return;
              if (onProductChange) onProductChange(next);
            }}
            disabled={!onProductChange}
            data-testid="admin-product-target-group"
            sx={{ display: 'flex', width: '100%' }}
          >
            {PRODUCT_TARGETS.map((t) => (
              <ToggleButton key={t} value={t} sx={{ flex: 1, textTransform: 'none', py: 0.4 }}
                data-testid={`admin-product-target-${t}`}>
                {PRODUCT_LABELS[t] || t}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Typography variant="caption"
            sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}
            data-testid="admin-product-target-build">
            {`This build ships: ${PRODUCT_LABELS[buildProduct] || buildProduct}`}
          </Typography>

          {/* ⚠ THE OVERRIDE SAYS SO, LOUDLY, and that is the half that earns its place. An
              administrator who forgets they are inspecting `astro` on an integralDelta build will
              report every astro icon as a deny-list failure. The warning names both values and
              the gesture that undoes it. */}
          {product !== buildProduct && (
            <Alert severity="warning" sx={{ mt: 1, py: 0 }}
              data-testid="admin-product-target-override">
              {`Inspecting ${PRODUCT_LABELS[product] || product} — this build ships `}
              {`${PRODUCT_LABELS[buildProduct] || buildProduct}. Nobody else sees this.`}
              {onProductChange && (
                <Button size="small" sx={{ ml: 1, py: 0 }}
                  data-testid="admin-product-target-reset"
                  onClick={() => onProductChange(buildProduct)}>
                  Reset
                </Button>
              )}
            </Alert>
          )}
        </Box>

        {toolGroups(TAB_SYSTEM)}
      </Box>

        {/* `pre-line` so a multi-line note keeps its breaks. The Eclipse Study returns a summary
            and a per-marker breakdown on two lines, and without this they run together into one
            paragraph in which the marker counts are unreadable. Harmless for every single-line
            note, which is what every other tool here returns. */}
        {note && (
          <Alert severity="info" sx={{ mt: 1, py: 0, whiteSpace: 'pre-line' }}
            data-testid="admin-panel-note">{note}</Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

AdminPanelDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  /** Renders nothing when false. The caller gates too — see the component note. */
  isAdmin: PropTypes.bool,
  /** TabPanel's own `handleIconClick`, so a relocated tool runs exactly what its icon ran. */
  onAction: PropTypes.func.isRequired,
  /**
   * The last clicked bar's index, or null.
   *
   * <p>Only read to DISABLE the tools that declare `needsAnchor` and to say why. The anchor
   * itself is resolved in App at dispatch time, so the panel cannot hand a stale index to a
   * tool — it only knows whether one exists.
   */
  anchorIndex: PropTypes.number,
  /**
   * Custom nodes to render INSTEAD of a tool's button, keyed by action.
   *
   * <p>For the relocated tools that are components rather than dispatched actions — see the note
   * in `toolGroups`. Absent, every tool renders the ordinary button, so a caller that does not
   * pass this is unaffected.
   */
  slots: PropTypes.objectOf(PropTypes.node),
  /** Returns `{year, from, to, bars}`, or null when no series is loaded. */
  onZoomFirstYear: PropTypes.func,
  tradeCardCount: PropTypes.number,
  openTradeCardCount: PropTypes.number,
  /** Takes a boolean, returns how many cards are now open. */
  onSetAllTradeCards: PropTypes.func,
  yearCount: PropTypes.number,
  summariesShown: PropTypes.bool,
  /** Takes a boolean, returns how many plaques are now shown. */
  onSetYearlySummaries: PropTypes.func,
  onEnterPresentationMode: PropTypes.func,
  /** Opens the Deep Pivot Research view. Admin only. */
  onOpenDeepPivot: PropTypes.func,
  onOpenCelestialAspects: PropTypes.func,
  /**
   * Runs the midpoint trine scan over the loaded range and paints it. Admin only — and the
   * SERVER enforces that too, with a 403, because a hidden button is not a guard.
   *
   * <p>May return a string to show, or a promise of one.
   */
  onRunMidpointTrineScan: PropTypes.func,
  onRunEclipseStudy: PropTypes.func,
  onRunMarketScanner: PropTypes.func,
  onOpenPlanetaryAnalog: PropTypes.func,
  onOpenPivotCommonalities: PropTypes.func,
  /** Opens the chart end-date dialog. Admin only — and the SERVER enforces that. */
  onOpenChartEndDates: PropTypes.func,
  /** The EFFECTIVE product target — already role-tested by the caller. One of PRODUCT_TARGETS. */
  product: PropTypes.oneOf([...PRODUCT_TARGETS]),
  /** What the bundle committed, shown beside the selector so an override is legible as one. */
  buildProduct: PropTypes.oneOf([...PRODUCT_TARGETS]),
  /** Switches the inspected product. Absent, the group renders DISABLED rather than missing. */
  onProductChange: PropTypes.func,
  /** Bars a CTS mark silences after it; 0 draws every flagged bar. */
  ctsCooldownBars: PropTypes.number,
  onCtsCooldownBarsChange: PropTypes.func,
};

AdminPanelDialog.defaultProps = {
  open: false,
  isAdmin: false,
  onZoomFirstYear: undefined,
  tradeCardCount: 0,
  openTradeCardCount: 0,
  onSetAllTradeCards: undefined,
  yearCount: 0,
  summariesShown: false,
  onSetYearlySummaries: undefined,
  onEnterPresentationMode: undefined,
  onOpenDeepPivot: undefined,
  onOpenCelestialAspects: undefined,
  onOpenChartEndDates: undefined,
};

export default AdminPanelDialog;
